import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  FileText,
  FileSpreadsheet,
  Share2,
  RotateCcw,
  Calendar,
  Building2,
  Truck,
  Boxes,
  Layers,
  Package,
  Repeat,
  CheckCircle2,
  IndianRupee,
  Clock,
  Coins,
  ChevronDown,
  Filter
} from 'lucide-react';
import { formatMoney } from '@vns/core';
import { useLookups } from '../lib/lookups';
import {
  useMaterialPickerList,
  useMaterialFullReportData,
  useAllMaterialsReportData,
  materialPurchasesSection,
  materialUsageSection,
  materialTransfersSection,
  materialProjectDistributionSection,
  materialSupplierSummarySection,
  materialFinancialSummarySection,
  allMaterialsStockSummarySection,
  allMaterialsPurchasesSection,
  allMaterialsUsageSection,
  allMaterialsTransfersSection,
  allMaterialsSupplierSection,
  allMaterialsFinancialSection,
  type MaterialReportSection,
} from '../lib/material-report';
import {
  exportExcelSections,
  exportPdfSections,
  sharePdfSections,
  canShare,
  exportExcelSection,
  exportPdfSection,
  sharePdfSection,
  type ExportSection,
  type ExportMeta,
} from '../lib/export';
import Shell from '../components/Shell';
import {
  EmptyState,
  TableSkeleton,
  ErrorState,
  useToast,
  Select,
} from '../components/ui';

const qty = (n: number) => n.toLocaleString('en-IN', { maximumFractionDigits: 3 });

export default function MaterialReport() {
  const navigate = useNavigate();
  const toast = useToast();
  const { data: picker, isLoading: pickerLoading } = useMaterialPickerList();
  const { data: lookups } = useLookups();

  const [materialId, setMaterialId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [ran, setRan] = useState(false);
  const [busy, setBusy] = useState<'excel' | 'pdf' | 'share' | null>(null);

  const isSingleMaterial = ran && !!materialId;
  const isAllMaterials = ran && !materialId;

  // Single material query
  const singleMaterialQuery = useMaterialFullReportData(isSingleMaterial ? materialId : '');

  // All materials query
  const allMaterialsQuery = useAllMaterialsReportData(isAllMaterials);

  const isLoading = ran && (isSingleMaterial ? singleMaterialQuery.isLoading : allMaterialsQuery.isLoading);
  const error = isSingleMaterial ? singleMaterialQuery.error : allMaterialsQuery.error;
  const refetch = () => {
    if (isSingleMaterial) void singleMaterialQuery.refetch();
    else void allMaterialsQuery.refetch();
  };

  const filtered = !!materialId || !!projectId || !!fromDate || !!toDate;
  const resetFilters = () => {
    setMaterialId('');
    setProjectId('');
    setFromDate('');
    setToDate('');
    setRan(false);
  };

  const narrow = (fn: (v: string) => void) => (v: string) => {
    fn(v);
    setRan(false);
  };

  type AnyRow = Record<string, unknown>;
  function applyFilters(dateKey: string, projectKey: string | null, rows: AnyRow[]): AnyRow[] {
    return rows.filter((r) => {
      // Date filter
      const d = String(r[dateKey] ?? '');
      if (fromDate && d < fromDate) return false;
      if (toDate && d > toDate) return false;
      // Project filter
      if (projectId) {
        if (projectKey === 'both_transfers') {
          if (r.from_project_id !== projectId && r.to_project_id !== projectId) return false;
        } else if (projectKey && r[projectKey] !== projectId) {
          return false;
        }
      }
      return true;
    });
  }

  // Filtered rows for Single Material
  const singleData = singleMaterialQuery.data;
  const singleFilteredPurchases = useMemo(
    () => applyFilters('purchased_on', 'project_id', singleData?.purchases ?? []),
    [singleData?.purchases, fromDate, toDate, projectId],
  );
  const singleFilteredUsage = useMemo(
    () => applyFilters('used_on', 'project_id', singleData?.usage ?? []),
    [singleData?.usage, fromDate, toDate, projectId],
  );
  const singleFilteredTransfers = useMemo(
    () => applyFilters('transferred_on', 'both_transfers', singleData?.transfers ?? []),
    [singleData?.transfers, fromDate, toDate, projectId],
  );

  // Filtered rows for All Materials
  const allData = allMaterialsQuery.data;
  const allFilteredPurchases = useMemo(
    () => applyFilters('purchased_on', 'project_id', allData?.purchases ?? []),
    [allData?.purchases, fromDate, toDate, projectId],
  );
  const allFilteredUsage = useMemo(
    () => applyFilters('used_on', 'project_id', allData?.usage ?? []),
    [allData?.usage, fromDate, toDate, projectId],
  );
  const allFilteredTransfers = useMemo(
    () => applyFilters('transferred_on', 'both_transfers', allData?.transfers ?? []),
    [allData?.transfers, fromDate, toDate, projectId],
  );

  const sections: MaterialReportSection[] = useMemo(() => {
    if (!ran) return [];

    if (isSingleMaterial && singleData) {
      const uName = singleData.unitName || '';
      return [
        materialPurchasesSection(singleFilteredPurchases, uName),
        materialUsageSection(singleFilteredUsage, uName, singleData.openingQty),
        materialTransfersSection(singleFilteredTransfers, uName),
        materialProjectDistributionSection(singleFilteredPurchases, singleFilteredUsage, singleFilteredTransfers, uName, singleData.ratePaise),
        materialSupplierSummarySection(singleFilteredPurchases, uName),
        materialFinancialSummarySection(singleData, singleFilteredPurchases),
      ];
    }

    if (isAllMaterials && allData) {
      return [
        allMaterialsStockSummarySection(allData.materials, allFilteredPurchases, allFilteredUsage, allFilteredTransfers),
        allMaterialsPurchasesSection(allFilteredPurchases),
        allMaterialsUsageSection(allFilteredUsage),
        allMaterialsTransfersSection(allFilteredTransfers),
        allMaterialsSupplierSection(allFilteredPurchases),
        allMaterialsFinancialSection(allFilteredPurchases, allData.materials, allFilteredUsage),
      ];
    }

    return [];
  }, [ran, isSingleMaterial, isAllMaterials, singleData, allData,
      singleFilteredPurchases, singleFilteredUsage, singleFilteredTransfers,
      allFilteredPurchases, allFilteredUsage, allFilteredTransfers]);

  function buildMeta(): ExportMeta {
    const filters: string[] = [];
    if (materialId) {
      const mName = picker?.find((m) => m.id === materialId)?.name;
      if (mName) filters.push(`Material: ${mName}`);
    } else {
      filters.push('Material: All Materials');
    }
    if (projectId) filters.push(`Project: ${lookups?.projects.find((p) => p.id === projectId)?.name ?? '—'}`);
    if (fromDate) filters.push(`From: ${fromDate}`);
    if (toDate) filters.push(`To: ${toDate}`);

    const selectedProjName = lookups?.projects.find((p) => p.id === projectId)?.name;
    const titleText = isSingleMaterial
      ? `Material Report \u2014 ${singleData?.name ?? ''}`
      : selectedProjName
      ? `Materials Report \u2014 ${selectedProjName}`
      : 'Materials Inventory & Consumption Report';

    return {
      title: titleText,
      subtitle: isSingleMaterial
        ? `Rate: ${formatMoney(singleData?.ratePaise ?? 0)} \u00b7 Unit: ${singleData?.unitName ?? '—'} \u00b7 Supplier: ${singleData?.supplierName ?? '—'}`
        : 'All Materials Overview',
      filters,
    };
  }

  async function runExport(kind: 'excel' | 'pdf') {
    if (!sections.length) return;
    setBusy(kind);
    try {
      const meta = buildMeta();
      if (kind === 'excel') {
        await exportExcelSections(sections, meta);
        toast.show('Excel report downloaded');
      } else {
        await exportPdfSections(sections, meta);
        toast.show('PDF report downloaded');
      }
    } catch (e) {
      toast.show(e instanceof Error ? e.message : 'Could not export the report', true);
    } finally {
      setBusy(null);
    }
  }

  async function runShare() {
    if (!sections.length) return;
    setBusy('share');
    try {
      const meta = buildMeta();
      const shared = await sharePdfSections(sections, meta);
      if (!shared) toast.show('Sharing is not supported on this device', true);
    } catch (e) {
      toast.show(e instanceof Error ? e.message : 'Could not share the report', true);
    } finally {
      setBusy(null);
    }
  }

  const ready = ran && !isLoading && !error && ((isSingleMaterial && !!singleData) || (isAllMaterials && !!allData));
  const sharing = canShare();
  const unit = singleData?.unitName ? ` ${singleData.unitName}` : '';
  const selectedProjectName = lookups?.projects.find((p) => p.id === projectId)?.name;

  // All materials multi-KPI calculations
  const totalAllPurchaseAmount = allFilteredPurchases.reduce((s, p) => s + Number(p.amount_paise), 0);
  const totalAllPurchaseCount = allFilteredPurchases.length;
  const totalAllUsageCount = allFilteredUsage.length;

  return (
    <Shell title="Material & Inventory Reports" crumb="Reports">
      <button
        className="btn btn-auto btn-sm btn-ghost"
        style={{
          marginBottom: 16,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          color: 'var(--text-soft)',
          paddingLeft: 4,
        }}
        onClick={() => navigate('/reports')}
      >
        <ArrowLeft size={14} />
        <span>Back to Reports Directory</span>
      </button>

      {/* Filter Parameters Form */}
      <div className="card" style={{ marginBottom: 20, padding: '20px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <Filter size={15} style={{ color: 'var(--brand)' }} />
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0, color: 'var(--text)' }}>
            Report Query Filters
          </h3>
        </div>

        <div
          className="form-grid"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}
        >
          <div className="field">
            <label>Material</label>
            <Select
              value={materialId}
              onChange={(val) => narrow(setMaterialId)(val)}
              disabled={pickerLoading}
              placeholder="All Materials (Summary & Ledgers)"
              options={[
                { value: '', label: 'All Materials (Summary & Ledgers)' },
                ...(picker?.map((m) => ({
                  value: m.id,
                  label: `${m.name}${m.brand ? ` (${m.brand})` : ''}`,
                })) ?? []),
              ]}
            />
          </div>

          <div className="field">
            <label>Project Site (Optional)</label>
            <Select
              value={projectId}
              onChange={(val) => narrow(setProjectId)(val)}
              placeholder="All Project Sites"
              options={[
                { value: '', label: 'All Project Sites' },
                ...(lookups?.projects.map((p) => ({
                  value: p.id,
                  label: p.name,
                })) ?? []),
              ]}
            />
          </div>

          <div className="field">
            <label>From Date</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setRan(false);
              }}
            />
          </div>

          <div className="field">
            <label>To Date</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setRan(false);
              }}
            />
          </div>
        </div>

        <div className="report-actions-bar">
          <div className="report-actions-primary">
            <button
              className="btn btn-primary btn-auto btn-sm"
              onClick={() => setRan(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, borderRadius: 'var(--radius-sm)' }}
            >
              <FileText size={14} />
              <span>Generate Report</span>
            </button>

            <button
              className="btn btn-auto btn-sm btn-ghost"
              onClick={resetFilters}
              disabled={!filtered}
              style={{ display: 'flex', alignItems: 'center', gap: 5, borderRadius: 'var(--radius-sm)' }}
            >
              <RotateCcw size={13} />
              <span>Reset</span>
            </button>
          </div>

          <div className="report-actions-exports">
            <button
              className="btn btn-auto btn-sm"
              disabled={!ready || !!busy}
              onClick={() => void runExport('excel')}
              style={{ display: 'flex', alignItems: 'center', gap: 6, borderRadius: 'var(--radius-sm)' }}
            >
              <FileSpreadsheet size={13} />
              <span>{busy === 'excel' ? 'Exporting…' : 'Export Excel'}</span>
            </button>

            <button
              className="btn btn-auto btn-sm"
              disabled={!ready || !!busy}
              onClick={() => void runExport('pdf')}
              style={{ display: 'flex', alignItems: 'center', gap: 6, borderRadius: 'var(--radius-sm)' }}
            >
              <FileText size={13} />
              <span>{busy === 'pdf' ? 'Exporting…' : 'Export PDF'}</span>
            </button>

            <button
              className="btn btn-auto btn-sm"
              disabled={!ready || !!busy}
              onClick={() => void runShare()}
              style={{ display: 'flex', alignItems: 'center', gap: 6, borderRadius: 'var(--radius-sm)' }}
            >
              <Share2 size={13} />
              <span>{busy === 'share' ? 'Sharing…' : 'Share'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Results Section */}
      {!ran ? (
        <div className="card" style={{ padding: '40px 20px' }}>
          <EmptyState
            icon="box"
            title="Click Generate Report to view material analytics"
            hint="You can filter by a single material, a specific construction site, or view an all-materials inventory audit."
          />
        </div>
      ) : isLoading ? (
        <div className="card">
          <TableSkeleton rows={6} cols={5} />
        </div>
      ) : error ? (
        <div className="card">
          <ErrorState error={error} onRetry={refetch} />
        </div>
      ) : isSingleMaterial && singleData ? (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
        >
          {/* Single Material Header */}
          <div
            className="card"
            style={{
              padding: '20px 24px',
              marginBottom: 20,
              background: 'var(--surface)',
              border: '1px solid var(--border)',
            }}
          >
            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0, color: 'var(--text)' }}>
                  {singleData.name}
                </h2>
                <div
                  style={{
                    display: 'flex',
                    gap: 16,
                    flexWrap: 'wrap',
                    marginTop: 8,
                    fontSize: 13,
                    color: 'var(--text-soft)',
                    alignItems: 'center',
                  }}
                >
                  {singleData.unitName && (
                    <span
                      style={{
                        fontSize: 11.5,
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: 'var(--radius-xs)',
                        background: 'var(--surface-muted)',
                        border: '1px solid var(--border)',
                        color: 'var(--text-soft)',
                      }}
                    >
                      Unit: {singleData.unitName}
                    </span>
                  )}
                  {singleData.brand && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <Layers size={13} style={{ color: 'var(--text-faint)' }} />
                      <span>Brand: {singleData.brand}</span>
                    </span>
                  )}
                  {singleData.supplierName && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <Truck size={13} style={{ color: 'var(--text-faint)' }} />
                      <span>Supplier: {singleData.supplierName}</span>
                    </span>
                  )}
                  {selectedProjectName && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <Building2 size={13} style={{ color: 'var(--text-faint)' }} />
                      <span>Project: {selectedProjectName}</span>
                    </span>
                  )}
                  {(fromDate || toDate) && (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                        background: 'rgba(37, 99, 235, 0.08)',
                        color: 'var(--brand)',
                        padding: '2px 9px',
                        borderRadius: 'var(--radius-xs)',
                        fontSize: 12,
                        fontWeight: 600,
                        border: '1px solid rgba(37, 99, 235, 0.2)',
                      }}
                    >
                      <Calendar size={12} />
                      {fromDate && toDate
                        ? `${fromDate} – ${toDate}`
                        : fromDate
                        ? `From ${fromDate}`
                        : `Up to ${toDate}`}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Single Material KPIs */}
          <div className="kpi-row" style={{ marginBottom: 20 }}>
            <div className="kpi">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <span className="lbl">Current Rate</span>
                <div style={{ color: 'var(--accent)', background: 'rgba(249, 115, 22, 0.1)', padding: 5, borderRadius: 'var(--radius-xs)' }}>
                  <IndianRupee size={14} />
                </div>
              </div>
              <div className="val num" style={{ fontSize: 20 }}>{formatMoney(singleData.ratePaise)}</div>
            </div>

            <div className="kpi">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <span className="lbl">Total Purchased</span>
                <div style={{ color: '#2563eb', background: 'rgba(37, 99, 235, 0.1)', padding: 5, borderRadius: 'var(--radius-xs)' }}>
                  <Truck size={14} />
                </div>
              </div>
              <div className="val num" style={{ fontSize: 20, color: '#2563eb' }}>
                {qty(singleData.totalPurchasedQty)}{unit}
              </div>
              <div className="sub num">{formatMoney(singleData.totalPurchasedAmountPaise)}</div>
            </div>

            <div className="kpi">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <span className="lbl">Total Used</span>
                <div style={{ color: '#8b5cf6', background: 'rgba(139, 92, 246, 0.1)', padding: 5, borderRadius: 'var(--radius-xs)' }}>
                  <Boxes size={14} />
                </div>
              </div>
              <div className="val num" style={{ fontSize: 20, color: '#8b5cf6' }}>
                {qty(singleData.totalUsedQty)}{unit}
              </div>
            </div>

            <div className="kpi">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <span className="lbl">Stock on Hand</span>
                <div
                  style={{
                    color: singleData.stockOnHand > 0 ? '#16a34a' : '#ea580c',
                    background: singleData.stockOnHand > 0 ? 'rgba(22, 163, 74, 0.1)' : 'rgba(234, 88, 12, 0.1)',
                    padding: 5,
                    borderRadius: 'var(--radius-xs)',
                  }}
                >
                  <Package size={14} />
                </div>
              </div>
              <div
                className="val num"
                style={{
                  fontSize: 20,
                  color: singleData.stockOnHand > 0 ? '#16a34a' : 'var(--text-soft)',
                }}
              >
                {qty(singleData.stockOnHand)}{unit}
              </div>
            </div>
          </div>

          {/* Accordion Sections */}
          <div className="accordion">
            {sections.map((s) => (
              <MaterialSectionAccordion
                key={s.key}
                section={s}
                meta={buildMeta()}
                sharing={sharing}
              />
            ))}
          </div>
        </motion.div>
      ) : isAllMaterials && allData ? (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
        >
          {/* All Materials Header */}
          <div
            className="card"
            style={{
              padding: '20px 24px',
              marginBottom: 20,
              background: 'var(--surface)',
              border: '1px solid var(--border)',
            }}
          >
            <div style={{ minWidth: 0 }}>
              <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0, color: 'var(--text)' }}>
                {selectedProjectName ? `Materials Report — ${selectedProjectName}` : 'All Materials Audit Report'}
              </h2>
              <div
                style={{
                  display: 'flex',
                  gap: 16,
                  flexWrap: 'wrap',
                  marginTop: 8,
                  fontSize: 13,
                  color: 'var(--text-soft)',
                  alignItems: 'center',
                }}
              >
                <span>{allData.materials.length} Materials in Catalogue</span>
                {selectedProjectName && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <Building2 size={13} style={{ color: 'var(--text-faint)' }} />
                    <span>Project: {selectedProjectName}</span>
                  </span>
                )}
                {(fromDate || toDate) && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      background: 'rgba(37, 99, 235, 0.08)',
                      color: 'var(--brand)',
                      padding: '2px 9px',
                      borderRadius: 'var(--radius-xs)',
                      fontSize: 12,
                      fontWeight: 600,
                      border: '1px solid rgba(37, 99, 235, 0.2)',
                    }}
                  >
                    <Calendar size={12} />
                    {fromDate && toDate
                      ? `${fromDate} – ${toDate}`
                      : fromDate
                      ? `From ${fromDate}`
                      : `Up to ${toDate}`}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* All Materials Multi-KPI */}
          <div className="kpi-row" style={{ marginBottom: 20 }}>
            <div className="kpi">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <span className="lbl">Materials Tracked</span>
                <div style={{ color: 'var(--brand)', background: 'rgba(37, 99, 235, 0.1)', padding: 5, borderRadius: 'var(--radius-xs)' }}>
                  <Package size={14} />
                </div>
              </div>
              <div className="val num" style={{ fontSize: 20 }}>{allData.materials.length}</div>
            </div>

            <div className="kpi">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <span className="lbl">Purchase Orders</span>
                <div style={{ color: '#2563eb', background: 'rgba(37, 99, 235, 0.1)', padding: 5, borderRadius: 'var(--radius-xs)' }}>
                  <Truck size={14} />
                </div>
              </div>
              <div className="val num" style={{ fontSize: 20, color: '#2563eb' }}>{totalAllPurchaseCount}</div>
              <div className="sub num">{formatMoney(totalAllPurchaseAmount)}</div>
            </div>

            <div className="kpi">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <span className="lbl">Site Usage Entries</span>
                <div style={{ color: '#8b5cf6', background: 'rgba(139, 92, 246, 0.1)', padding: 5, borderRadius: 'var(--radius-xs)' }}>
                  <Boxes size={14} />
                </div>
              </div>
              <div className="val num" style={{ fontSize: 20, color: '#8b5cf6' }}>{totalAllUsageCount}</div>
            </div>

            <div className="kpi">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <span className="lbl">Stock Transfers</span>
                <div style={{ color: 'var(--accent)', background: 'rgba(249, 115, 22, 0.1)', padding: 5, borderRadius: 'var(--radius-xs)' }}>
                  <Repeat size={14} />
                </div>
              </div>
              <div className="val num" style={{ fontSize: 20 }}>{allFilteredTransfers.length}</div>
            </div>
          </div>

          {/* Accordions */}
          <div className="accordion">
            {sections.map((s) => (
              <MaterialSectionAccordion
                key={s.key}
                section={s}
                meta={buildMeta()}
                sharing={sharing}
              />
            ))}
          </div>
        </motion.div>
      ) : (
        <div className="card">
          <EmptyState icon="box" title="No material data found" />
        </div>
      )}
    </Shell>
  );
}

/* ------------------------------------------------------------------ */
/* Material Section Accordion Component                                */
/* ------------------------------------------------------------------ */

function MaterialSectionAccordion({
  section,
  meta,
  sharing,
}: {
  section: MaterialReportSection;
  meta: ExportMeta;
  sharing: boolean;
}) {
  const toast = useToast();
  const [secBusy, setSecBusy] = useState<'excel' | 'pdf' | 'share' | null>(null);
  const hasRows = section.rows.length > 0;

  async function doSectionExport(kind: 'excel' | 'pdf') {
    setSecBusy(kind);
    try {
      if (kind === 'excel') {
        await exportExcelSection(section as ExportSection, meta);
        toast.show('Excel downloaded');
      } else {
        await exportPdfSection(section as ExportSection, meta);
        toast.show('PDF downloaded');
      }
    } catch (e) {
      toast.show(e instanceof Error ? e.message : 'Could not create the file', true);
    } finally {
      setSecBusy(null);
    }
  }

  async function doSectionShare() {
    setSecBusy('share');
    try {
      const shared = await sharePdfSection(section as ExportSection, meta);
      if (!shared) {
        await exportPdfSection(section as ExportSection, meta);
        toast.show('PDF downloaded');
      }
    } catch (e) {
      toast.show(e instanceof Error ? e.message : 'Could not share', true);
    } finally {
      setSecBusy(null);
    }
  }

  return (
    <details className="accordion-item" open>
      <summary className="accordion-summary">
        <div className="accordion-icon-box">
          <Package size={18} className="accordion-icon" />
        </div>
        <span className="accordion-title">{section.title}</span>
        <span className="count-badge">
          {section.rows.length} record{section.rows.length === 1 ? '' : 's'}
        </span>

        {/* Action buttons inside accordion summary (Desktop / Tablet view) */}
        <div
          className="section-actions desktop-only"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="btn btn-auto btn-sm btn-ghost"
            disabled={!hasRows || !!secBusy}
            title={`Download ${section.title} as Excel`}
            onClick={() => void doSectionExport('excel')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', fontSize: 12 }}
          >
            <FileSpreadsheet size={13} />
            <span className="section-btn-label">Excel</span>
          </button>
          <button
            className="btn btn-auto btn-sm btn-ghost"
            disabled={!hasRows || !!secBusy}
            title={`Download ${section.title} as PDF`}
            onClick={() => void doSectionExport('pdf')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', fontSize: 12 }}
          >
            <FileText size={13} />
            <span className="section-btn-label">PDF</span>
          </button>
          <button
            className="btn btn-auto btn-sm btn-ghost"
            disabled={!hasRows || !!secBusy}
            title={`Share ${section.title}`}
            onClick={() => void doSectionShare()}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', fontSize: 12 }}
          >
            <Share2 size={13} />
            <span className="section-btn-label">Share</span>
          </button>
        </div>

        <ChevronDown size={16} className="accordion-chevron" />
      </summary>

      <div className="accordion-body">
        {!section.rows.length ? (
          <EmptyState icon="box" title="No entries recorded for this section" />
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    {section.columns.map((c) => (
                      <th key={c.key}>
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {section.rows.map((r, i) => (
                    <tr key={i}>
                      {section.columns.map((c) => (
                        <td key={c.key} className={c.numeric ? 'num' : undefined}>
                          {String(r[c.key] ?? '—')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Table Footer Actions placed at the end of each table */}
            <div className="table-footer-actions mobile-only">
              <button
                className="btn btn-auto btn-sm table-footer-btn"
                disabled={!hasRows || !!secBusy}
                onClick={() => void doSectionExport('excel')}
              >
                <FileSpreadsheet size={13} />
                <span>Export Excel</span>
              </button>
              <button
                className="btn btn-auto btn-sm table-footer-btn"
                disabled={!hasRows || !!secBusy}
                onClick={() => void doSectionExport('pdf')}
              >
                <FileText size={13} />
                <span>Export PDF</span>
              </button>
              <button
                className="btn btn-auto btn-sm table-footer-btn"
                disabled={!hasRows || !!secBusy}
                onClick={() => void doSectionShare()}
              >
                <Share2 size={13} />
                <span>Share</span>
              </button>
            </div>
          </>
        )}
      </div>
    </details>
  );
}
